import React, { useEffect, useMemo, useRef, useState } from 'react';
import { insuranceApi } from './api';
import { useExtractionPolling, useProviders } from './hooks';
import { COVERAGE_OPTIONS } from './insuranceConfig';
import { fmtMxn, fmtPeriodLabel } from './designHelpers';
import ModalShell from './ModalShell';
import { uploadFileToS3 } from '../../../../../../Utils/s3Upload';

// Map document_type IA → tipo legacy del dropdown
function aiDocTypeToLegacy(dt) {
  if (dt === 'policy' || dt === 'policy_and_payments') return 'poliza';
  if (dt === 'payments') return 'pagare';
  if (dt === 'unrelated') return 'otro';
  return null;
}

// 10 MB. Antes decía 25 MB, pero era ficción: Lambda cortaba en ~4.4 MB con un
// 413. Ahora la subida va directo a S3 y ese techo desapareció; el tope se
// mantiene bajo a propósito mientras el OCR de Textract siga pudiendo tardar
// más que el timeout del job (ExtractAssetDataJob::$timeout).
// Debe coincidir con InsuranceStepHandler::MAX_UPLOAD_BYTES.
const MAX_BYTES = 10 * 1024 * 1024;
const TOLERANCIA_SUMA = 1; // pesos — coincide con la validación visual local

const DOC_TYPES = [
  { value: 'poliza',  label: 'Póliza'  },
  { value: 'pagare',  label: 'Pagaré'  },
  { value: 'endoso',  label: 'Endoso'  },
  { value: 'otro',    label: 'Otro'    },
];

/* ──────────────────────────────────────────────────────────
 * Utilities
 * ────────────────────────────────────────────────────────── */

function detectTypeFromName(name = '') {
  const n = name.toLowerCase();
  if (n.includes('pagar')) return 'pagare';
  if (n.includes('poliz')) return 'poliza';
  if (n.includes('endos')) return 'endoso';
  return 'otro';
}

function detectYearFromName(name = '') {
  // intenta "año 2", "ano 2", "year 2", "p2", "_2_", "-2-"
  const n = name.toLowerCase();
  const m = n.match(/(?:a(?:n|ñ)o|year|p|y)[\s_-]*(\d{1,2})/) || n.match(/[_\-](\d{1,2})(?=[._\-])/);
  if (m) {
    const v = parseInt(m[1], 10);
    if (v >= 1 && v <= 10) return v;
  }
  return 1;
}

function toDateInputValue(iso) {
  if (!iso) return '';
  try { return new Date(iso).toISOString().slice(0, 10); }
  catch { return String(iso).slice(0, 10); }
}

function formatBytes(b) {
  if (!b && b !== 0) return '';
  const k = 1024;
  if (b < k) return b + ' B';
  if (b < k * k) return Math.round(b / k) + ' KB';
  return (b / (k * k)).toFixed(1) + ' MB';
}

function addYears(isoDate, n) {
  if (!isoDate) return '';
  const d = new Date(isoDate);
  if (isNaN(d.getTime())) return '';
  d.setFullYear(d.getFullYear() + n);
  return d.toISOString().slice(0, 10);
}

function yearOf(iso) {
  if (!iso) return null;
  const d = new Date(iso);
  if (!isNaN(d.getTime())) return d.getFullYear();
  const m = String(iso).match(/^(\d{4})/);
  return m ? parseInt(m[1], 10) : null;
}

/* ──────────────────────────────────────────────────────────
 * Componente principal
 * ────────────────────────────────────────────────────────── */

export default function UploadResponseModal({
  visible, onHide,
  qpId, cotizadorPrice, view, toast, onCompleted,
}) {
  const { providers } = useProviders('insurance');

  // sub-step: 'drop' | 'processing' | 'review'
  const [step, setStep] = useState('drop');
  const [files, setFiles] = useState([]); // [{id, file, nombre, size, type, year, doc?:{id,...}, aiBadge:bool}]
  const [useAi, setUseAi] = useState(true);
  const [draggedOver, setDraggedOver] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadResult, setUploadResult] = useState(null);
  const [completing, setCompleting] = useState(false);

  // Datos generales póliza
  const [policyNumber, setPolicyNumber] = useState('');
  // Nombre de aseguradora detectado por IA (ej. "GNP"). Se manda como
  // provider_name_free si el admin no eligió uno del dropdown — así el
  // backend sobrescribe el `company` ("LogoGnp.gif") con la versión limpia.
  const [aiCompany, setAiCompany] = useState('');
  const [coverageType, setCoverageType] = useState('Amplia');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [annualTotal, setAnnualTotal] = useState(''); // suma esperada (insurance.amount)
  const [paymentPlanYears, setPaymentPlanYears] = useState(1);
  const [providerId, setProviderId] = useState(view?.aseguradora?.providerObj?.id ?? '');
  const [providerFree, setProviderFree] = useState('');

  // Pagarés capturados: { [periodNumber]: { period_number, period_label, due_date, amount, reference_number, document_local_id, ai_extracted_data, ai_status } }
  const [payments, setPayments] = useState({});

  // Banners IA por documento procesado: { [docId]: { lowConfidence, failed, accuracy, finding, documentType, unrelated } }
  const [aiBanners, setAiBanners] = useState({});

  // Warnings globales (unión de validation.warnings de todos los docs)
  const [globalWarnings, setGlobalWarnings] = useState([]);

  // Tracking de cuales documents ya prellenamos para evitar pisar capturas del admin
  const prefilledDocsRef = useRef(new Set());
  const fileInputRef = useRef(null);

  // ── Reset al cerrar ───────────────────────────────────────
  useEffect(() => {
    if (!visible) {
      setStep('drop');
      setFiles([]);
      setUseAi(true);
      setUploadResult(null);
      setPolicyNumber('');
      setAiCompany('');
      setCoverageType('Amplia');
      setStartDate('');
      setEndDate('');
      setAnnualTotal('');
      setPaymentPlanYears(1);
      setProviderId(view?.aseguradora?.providerObj?.id ?? '');
      setProviderFree('');
      setPayments({});
      setAiBanners({});
      setGlobalWarnings([]);
      prefilledDocsRef.current = new Set();
    }
  }, [visible]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Sincronizar el número de tarjetas con paymentPlanYears ──
  useEffect(() => {
    const years = Math.max(1, Math.min(10, Number(paymentPlanYears) || 1));
    setPayments((prev) => {
      const next = { ...prev };
      // crear faltantes
      for (let i = 1; i <= years; i++) {
        if (!next[i]) {
          const start = addYears(startDate, i - 1) || '';
          const due = addYears(start, 1) || '';
          next[i] = {
            period_number: i,
            period_label: fmtPeriodLabel(i, start),
            due_date: due,
            period_start: start,
            period_end: due,
            amount: '',
            reference_number: '',
            document_local_id: null,
            ai_extracted_data: null,
            ai_status: 'pending',
          };
        }
      }
      // borrar extras (pero respetando los que ya tienen captura)
      Object.keys(next).forEach((k) => {
        const n = parseInt(k, 10);
        if (n > years) delete next[n];
      });
      return next;
    });
  }, [paymentPlanYears, startDate]);

  // Cuando cambia startDate, refrescar due_date y label SOLO para los que NO han sido tocados por el admin (heurística: que aún sigan iguales al "auto" anterior).
  // Para no destruir captura, no pisamos due_date capturado: solo si está vacío.
  useEffect(() => {
    if (!startDate) return;
    setPayments((prev) => {
      const next = { ...prev };
      Object.keys(next).forEach((k) => {
        const p = next[k];
        if (!p) return;
        if (!p.due_date) {
          const start = addYears(startDate, (p.period_number || 1) - 1);
          const due = addYears(start, 1);
          next[k] = {
            ...p,
            due_date: due,
            period_start: start,
            period_end: due,
            period_label: fmtPeriodLabel(p.period_number, start)
          };
        }
      });
      return next;
    });
  }, [startDate]);

  // ── Polling AI para los documentos subidos ────────────────
  const uploadedDocs = uploadResult?.documents ?? [];

  // ── Mismo handler de extracción para los N documentos: hacemos un componente interno que renderiza un hook por documento. (Ver MultiDocAIPolling al final.)

  // ── Banners derivados ─────────────────────────────────────
  const anyLowConfidence = useMemo(
    () => Object.values(aiBanners).some((b) => b?.lowConfidence),
    [aiBanners]
  );

  // ── Sumatoria de pagarés ──────────────────────────────────
  const paymentsArray = useMemo(() => {
    const years = Math.max(1, Math.min(10, Number(paymentPlanYears) || 1));
    return Array.from({ length: years }, (_, i) => payments[i + 1]).filter(Boolean);
  }, [payments, paymentPlanYears]);

  const paymentsSum = useMemo(
    () => paymentsArray.reduce((acc, p) => acc + (Number(p?.amount) || 0), 0),
    [paymentsArray]
  );

  // El total final es SIEMPRE la suma de los pagarés (los pagarés son la
  // fuente de verdad). `annualTotal` solo guarda lo que la IA dijo, para
  // mostrar un aviso si difiere de la suma real.
  const totalFinal = paymentsSum;
  const aiTotal = Number(annualTotal) || 0;
  const aiTotalMismatch = aiTotal > 0 && Math.abs(aiTotal - totalFinal) > 0.01;
  // Para retro-compatibilidad con el footer y otros consumidores:
  const totalEsperado = totalFinal;
  const sumDiff = 0;
  const sumOk = totalFinal > 0;

  // ── Validaciones generales ────────────────────────────────
  const generalOk = useMemo(() => (
    !!policyNumber && !!startDate && !!endDate && paymentPlanYears >= 1
  ), [policyNumber, startDate, endDate, paymentPlanYears]);

  const paymentsOk = useMemo(() => {
    if (paymentsArray.length === 0) return false;
    return paymentsArray.every((p) => p && Number(p.amount) > 0 && !!p.due_date);
  }, [paymentsArray]);

  const canComplete = (
    generalOk &&
    paymentsOk &&
    sumOk &&
    uploadedDocs.length > 0 &&
    !completing
  );

  // ── Handlers ─────────────────────────────────────────────
  const handleSelectFiles = (incoming) => {
    const list = Array.from(incoming || [])
      .filter((file) => file.type === 'application/pdf' || file.name.toLowerCase().endsWith('.pdf'))
      .filter((file) => file.size <= MAX_BYTES)
      .map((file) => {
        const t = detectTypeFromName(file.name);
        return {
          id: 'f-' + Date.now() + '-' + Math.random().toString(36).slice(2, 8),
          file,
          nombre: file.name,
          size: formatBytes(file.size),
          type: t,
          year: t === 'pagare' ? detectYearFromName(file.name) : null,
          aiBadge: t === 'poliza' || t === 'pagare',
        };
      });
    setFiles((prev) => [...prev, ...list]);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDraggedOver(false);
    handleSelectFiles(e.dataTransfer?.files);
  };

  const processFiles = async () => {
    if (!files.length) return;
    setUploading(true);
    try {
      // label: usa el "type" para que el backend categorize (poliza/pagare/endoso/otro)
      // y opcionalmente sufija el año si es pagaré: "pagare año 2"
      const labelFor = (f) =>
        f.type === 'pagare' && f.year ? `pagare año ${f.year}` : f.type;

      // El PDF no viaja por la API: se pide una URL prefirmada, el navegador
      // sube directo a S3 y al backend solo le llega el key. Así se evita el
      // 413 de Lambda, que corta en ~4.4 MB.
      const { data: urlsRes } = await insuranceApi.getUploadUrls(qpId, {
        files: files.map((f) => ({
          file_name: f.file.name,
          mime_type: 'application/pdf',
        })),
      });

      const uploads = urlsRes?.uploads || [];
      if (uploads.length !== files.length) {
        throw new Error('El servidor no entregó una URL de subida por archivo.');
      }

      // Secuencial a propósito: son pocos archivos y pesados, y saturar la
      // conexión hace que los reintentos sean más probables, no menos.
      for (let i = 0; i < files.length; i += 1) {
        // mime_type se manda fijo porque file.type viene vacío para PDFs en
        // algunos sistemas, y el backend valida contra lo que S3 haya guardado.
        await uploadFileToS3(files[i].file, uploads[i].url, {
          contentType: 'application/pdf',
        });
      }

      const res = await insuranceApi.uploadKeys(qpId, {
        files: files.map((f, i) => ({
          s3_key: uploads[i].key,
          file_name: f.file.name,
          label: labelFor(f),
        })),
        use_ai: useAi,
      });

      if (res?.data?.success) {
        const docs = res.data.documents || [];
        setUploadResult({
          documents: docs,
          extraction: res.data.extraction || null,
        });
        // mapear local id ↔ doc id por orden
        setFiles((prev) => prev.map((f, i) => ({ ...f, doc: docs[i] || null })));
        setStep(useAi ? 'processing' : 'review');
        if (!useAi) {
          // si no se usa IA pasamos directo a review
        }
      }
    } catch (e) {
      toast?.current?.show?.({
        severity: 'error',
        summary: 'Error al subir',
        detail: e?.response?.data?.message || e.message,
      });
    } finally {
      setUploading(false);
    }
  };

  // ── Cuando termina la subida sin IA, pasar a review ───────
  useEffect(() => {
    if (step === 'processing' && !useAi && uploadResult) setStep('review');
  }, [step, useAi, uploadResult]);

  // ── Callback de cada doc cuando la IA termina ─────────────
  // Reglas (formato unificado SEGURO):
  //  · El propio prompt auto-clasifica: document_type ∈
  //    {'policy','payments','policy_and_payments','unrelated'}
  //  · Si trae policy → precarga campos generales de la póliza.
  //  · Si trae payments[] → precarga las tarjetas (proyectados vs reales).
  //  · 'unrelated' → no precarga nada; UI lo pinta en gris.
  //  · accuracy < 30 o status='failed' → banner amarillo (no bloquea, no vacía).
  //  · El dropdown "Tipo" del admin se auto-preselecciona al detectar.
  const handleAIResult = (localFile, docId, { status, data, accuracy, finding, documentType, policy, paymentsDetected, warnings }) => {
    if (!docId) return;
    const key = String(docId);
    setAiBanners((prev) => ({
      ...prev,
      [key]: {
        lowConfidence: status === 'done' && accuracy != null && accuracy < 30,
        failed: status === 'failed',
        accuracy,
        finding,
        documentType: documentType || null,
        unrelated: documentType === 'unrelated',
      },
    }));

    // Acumular warnings globales (validation.warnings del prompt unificado)
    if (Array.isArray(warnings) && warnings.length > 0) {
      setGlobalWarnings((prev) => {
        const merged = new Set(prev);
        warnings.forEach((w) => { if (w) merged.add(`[${localFile?.nombre || docId}] ${w}`); });
        return Array.from(merged);
      });
    }

    if (status !== 'done') return;
    if (prefilledDocsRef.current.has(key)) return;
    prefilledDocsRef.current.add(key);

    // Override del dropdown del admin con la detección IA (badge "detectado")
    const aiLegacyType = aiDocTypeToLegacy(documentType);
    if (aiLegacyType && localFile?.id) {
      setFiles((prev) => prev.map((f) => f.id === localFile.id ? {
        ...f,
        type: aiLegacyType,
        aiDetected: true,
        aiUnrelated: documentType === 'unrelated',
      } : f));
    }

    // Documento ajeno: no precargar nada
    if (documentType === 'unrelated') return;

    // ── PRECARGA POLICY ──────────────────────────────────────
    const pol = policy || null;
    if (pol && (documentType === 'policy' || documentType === 'policy_and_payments')) {
      setPolicyNumber((curr) => curr || (pol.policy_number || ''));
      if (pol.company) setAiCompany((curr) => curr || String(pol.company).trim());
      setCoverageType((curr) => curr || (pol.coverage_type || curr));
      setStartDate((curr) => curr || (pol.start_date ? toDateInputValue(pol.start_date) : ''));
      setEndDate((curr) => curr || (pol.end_date ? toDateInputValue(pol.end_date) : ''));
      if (pol.payment_plan_years) {
        setPaymentPlanYears((curr) => (curr > 1 ? curr : Number(pol.payment_plan_years) || 1));
      }
      const total = pol.annual_amount_total ?? null;
      if (total != null) setAnnualTotal((curr) => curr || String(total));
    }

    // ── PRECARGA PAYMENTS ─────────────────────────────────────
    const list = Array.isArray(paymentsDetected) ? paymentsDetected : [];
    if (list.length > 0) {
      setPayments((prev) => {
        const next = { ...prev };
        list.forEach((d, idx) => {
          const pn = Number(d.period_number) || Number(localFile?.year) || (idx + 1);
          if (!pn) return;
          const existing = next[pn] || {
            period_number: pn,
            period_label: '',
            due_date: '',
            period_start: '',
            period_end: '',
            amount: '',
            reference_number: '',
            document_local_id: null,
            ai_extracted_data: null,
            ai_status: 'pending',
            projected: false,
            due_date_inferred: false,
          };
          // Si el existente es proyectado y este es real → gana el real.
          const existingProjected = !!existing.projected;
          const candidateProjected = !!d.projected;
          const realReplacesProjected = existingProjected && !candidateProjected;

          next[pn] = {
            ...existing,
            due_date: (existing.due_date && !realReplacesProjected)
              ? existing.due_date
              : (d.due_date ? toDateInputValue(d.due_date) : (existing.due_date || '')),
            period_start: (existing.period_start && !realReplacesProjected)
              ? existing.period_start
              : (d.period_start ? toDateInputValue(d.period_start) : (existing.period_start || '')),
            period_end: (existing.period_end && !realReplacesProjected)
              ? existing.period_end
              : (d.period_end ? toDateInputValue(d.period_end) : (existing.period_end || '')),
            amount: (existing.amount && !realReplacesProjected)
              ? existing.amount
              : (d.amount != null ? String(d.amount) : (existing.amount || '')),
            reference_number: existing.reference_number || (d.reference_number || ''),
            period_label: existing.period_label || fmtPeriodLabel(pn, d.due_date),
            ai_extracted_data: d,
            ai_status: 'done',
            projected: realReplacesProjected ? false : (candidateProjected || existingProjected),
            due_date_inferred: !!d.due_date_inferred,
            // Si no hay PDF asignado, asignar el de este doc
            document_local_id: existing.document_local_id || localFile?.id || null,
          };
        });
        return next;
      });
    }
  };

  // ── Cuando TODOS los docs terminan (o si no hay IA), pasar a review ──
  const allDocIds = uploadedDocs.map((d) => String(d.id));
  const allDone = useAi && allDocIds.length > 0
    ? allDocIds.every((id) => {
        const b = aiBanners[id];
        return b && (b.failed || b.accuracy != null || b.lowConfidence === false || b.lowConfidence === true);
      })
    : false;

  useEffect(() => {
    if (step === 'processing' && useAi && allDone) {
      setStep('review');
    }
  }, [step, useAi, allDone]);

  // ── Auto-asignar PDFs a tarjetas vacías ──────────────────
  // Si solo hay un PDF de tipo "poliza", usar como default en todas las tarjetas que no tengan asignación.
  // Excluye docs marcados como 'unrelated' por la IA.
  useEffect(() => {
    if (step !== 'review') return;
    const polizaFiles = files.filter((f) => f.type === 'poliza' && f.doc && !f.aiUnrelated);
    const pagareFiles = files.filter((f) => f.type === 'pagare' && f.doc && !f.aiUnrelated);

    setPayments((prev) => {
      const next = { ...prev };
      Object.keys(next).forEach((k) => {
        const p = next[k];
        if (!p || p.document_local_id) return;
        // 1) si hay un pagaré con el mismo año, usar ese
        const matchPagare = pagareFiles.find((pf) => Number(pf.year) === Number(p.period_number));
        if (matchPagare) {
          next[k] = { ...p, document_local_id: matchPagare.id };
          return;
        }
        // 2) si hay un solo PDF de póliza, usar ese
        if (polizaFiles.length === 1) {
          next[k] = { ...p, document_local_id: polizaFiles[0].id };
        }
      });
      return next;
    });
  }, [step, files]);

  // ── Submit final ─────────────────────────────────────────
  const handleComplete = async () => {
    if (!canComplete) return;
    setCompleting(true);
    try {
      // El total se calcula desde los pagarés (fuente de verdad), no del
      // valor que pudo dar la IA. Eso garantiza que SUM(payments) == total.
      const total = paymentsSum;
      const paymentsPayload = paymentsArray.map((p) => {
        const localFile = files.find((f) => f.id === p.document_local_id) || null;
        const docId = localFile?.doc?.id ?? null;
        return {
          period_number: Number(p.period_number),
          period_label: p.period_label || fmtPeriodLabel(p.period_number, p.due_date),
          due_date: p.due_date,
          period_start: p.period_start || null,
          period_end: p.period_end || null,
          amount: Number(p.amount),
          reference_number: p.reference_number || null,
          document_id: docId,
          ai_extracted_data: p.ai_extracted_data || null,
          ai_status: p.ai_status || 'pending',
        };
      });

      const payload = {
        policy_number: policyNumber,
        coverage_type: coverageType || null,
        start_date: startDate,
        end_date: endDate,
        annual_price: total, // total para validación legacy
        amount: total,       // explícito por compatibilidad
        payment_plan_years: Number(paymentPlanYears),
        provider_id: providerId && providerId !== '__free__' ? providerId : null,
        // provider_name_free se usa para sobrescribir `insurances.company` con
        // un nombre limpio: prioriza la captura manual del admin ("Otro"),
        // si no, usa lo que IA detectó (ej. "GNP" en mayúsculas).
        provider_name_free: providerId === '__free__'
          ? providerFree
          : (aiCompany || null),
        document_ids: uploadedDocs.map((d) => d.id),
        cotizador_price: cotizadorPrice ?? null,
        source: 'manual',
        ai_status: useAi ? 'done' : 'disabled',
        payments: paymentsPayload,
      };

      const res = await insuranceApi.complete(qpId, payload);
      if (res?.data?.success) {
        toast?.current?.show?.({
          severity: 'success',
          summary: '¡Seguro activado!',
          detail: 'Insurance creado correctamente',
          life: 3500,
        });
        onCompleted?.();
        onHide?.();
      }
    } catch (e) {
      toast?.current?.show?.({
        severity: 'error',
        summary: 'No se pudo completar',
        detail: e?.response?.data?.message || e.message,
      });
    } finally {
      setCompleting(false);
    }
  };

  // ── Render ───────────────────────────────────────────────
  return (
    <ModalShell visible={visible} onClose={onHide} stacked width={760}>
      <button className="modal-close" onClick={onHide}><i className="pi pi-times" /></button>

      {(step === 'processing' || uploading) && (
        <div style={{ height: 3, background: 'var(--pei-gray-5)', position: 'relative', overflow: 'hidden' }}>
          <div style={{
            position: 'absolute', top: 0, left: 0, height: '100%', width: '40%',
            background: 'var(--pei-gradient-cyan-green)',
            animation: 'seg-indeter 1.4s ease-in-out infinite',
          }} />
        </div>
      )}

      <div style={{ padding: '26px 32px 14px' }}>
        <div className="eyebrow" style={{ marginBottom: 6 }}>Paso 5 · Seguros</div>
        <div style={{ fontSize: 22, fontWeight: 700, color: 'var(--pei-text)', letterSpacing: '-0.008em' }}>
          {step === 'drop' && 'Subir póliza y pagarés'}
          {step === 'processing' && 'Procesando tus archivos…'}
          {step === 'review' && 'Revisa los datos extraídos'}
        </div>
        <div style={{ fontSize: 13, color: 'var(--pei-text-helper)', marginTop: 4 }}>
          {view?.cliente} · {view?.vehiculo}
        </div>
      </div>

      <div className="scrollable" style={{ padding: '0 32px 14px', flex: 1, minHeight: 0 }}>
        {step === 'drop' && (
          <DropStep
            files={files}
            setFiles={setFiles}
            useAi={useAi}
            setUseAi={setUseAi}
            draggedOver={draggedOver}
            setDraggedOver={setDraggedOver}
            fileInputRef={fileInputRef}
            handleSelectFiles={handleSelectFiles}
            handleDrop={handleDrop}
          />
        )}

        {step === 'processing' && (
          <ProcessingStep files={files} useAi={useAi} aiBanners={aiBanners} />
        )}

        {step === 'review' && (
          <ReviewStep
            files={files}
            providers={providers}
            providerId={providerId} setProviderId={setProviderId}
            providerFree={providerFree} setProviderFree={setProviderFree}
            policyNumber={policyNumber} setPolicyNumber={setPolicyNumber}
            coverageType={coverageType} setCoverageType={setCoverageType}
            startDate={startDate} setStartDate={setStartDate}
            endDate={endDate} setEndDate={setEndDate}
            annualTotal={annualTotal} setAnnualTotal={setAnnualTotal}
            paymentPlanYears={paymentPlanYears} setPaymentPlanYears={setPaymentPlanYears}
            payments={payments} setPayments={setPayments}
            paymentsSum={paymentsSum}
            totalEsperado={totalEsperado}
            sumDiff={sumDiff}
            sumOk={sumOk}
            totalFinal={totalFinal}
            aiTotal={aiTotal}
            aiTotalMismatch={aiTotalMismatch}
            anyLowConfidence={anyLowConfidence}
            useAi={useAi}
            cotizadorPrice={cotizadorPrice}
            aiBanners={aiBanners}
            globalWarnings={globalWarnings}
          />
        )}
      </div>

      {/* Poller invisible: monta un hook por documento mientras estamos en processing/review */}
      {useAi && uploadedDocs.length > 0 && (step === 'processing' || step === 'review') && (
        <MultiDocAIPolling
          docs={uploadedDocs}
          files={files}
          onResult={handleAIResult}
        />
      )}

      <div style={{ padding: '14px 32px 22px', borderTop: '1px solid var(--pei-gray-5)', display: 'flex', alignItems: 'center', gap: 10 }}>
        {step === 'drop' && (
          <>
            <button className="pei-btn pei-btn-ghost" onClick={onHide} disabled={uploading}>Cancelar</button>
            <div style={{ flex: 1 }} />
            <button className="pei-btn pei-btn-primary floor-shadow" disabled={files.length === 0 || uploading} onClick={processFiles}>
              {uploading ? 'Subiendo…' : useAi
                ? <>Procesar con IA <i className="pi pi-bolt" style={{ fontSize: 12 }} /></>
                : <>Continuar <i className="pi pi-arrow-right" style={{ fontSize: 12 }} /></>}
            </button>
          </>
        )}
        {step === 'processing' && (
          <>
            <button className="pei-btn pei-btn-ghost" disabled>Cancelar</button>
            <div style={{ flex: 1, fontSize: 12, color: 'var(--pei-text-helper)', textAlign: 'center' }}>
              No cierres la ventana
            </div>
            <button className="pei-btn pei-btn-ring" onClick={() => setStep('review')}>
              Saltar a revisar →
            </button>
          </>
        )}
        {step === 'review' && (
          <>
            <button className="pei-btn pei-btn-ghost" onClick={() => setStep('drop')}>← Volver</button>
            <div style={{ flex: 1, fontSize: 12, color: sumOk ? 'var(--pei-green-true)' : 'var(--pei-danger)' }}>
              {!totalEsperado
                ? 'Captura el total para validar la suma'
                : sumOk
                  ? `Suma pagarés: ${fmtMxn(paymentsSum)} · coincide con total`
                  : `Suma pagarés: ${fmtMxn(paymentsSum)} · difiere por ${fmtMxn(Math.abs(sumDiff))}`}
            </div>
            <button
              className="pei-btn pei-btn-primary floor-shadow"
              onClick={handleComplete}
              disabled={!canComplete}
            >
              <i className="pi pi-check" style={{ fontSize: 12 }} />
              {completing ? 'Completando…' : 'Completar paso'}
            </button>
          </>
        )}
      </div>
    </ModalShell>
  );
}

/* ──────────────────────────────────────────────────────────
 * Multi-doc AI polling: monta un hook por documento.
 * ────────────────────────────────────────────────────────── */
function MultiDocAIPolling({ docs, files, onResult }) {
  return (
    <>
      {docs.map((d) => {
        const f = files.find((x) => x.doc?.id === d.id);
        return <SingleDocPoller key={d.id} doc={d} localFile={f} onResult={onResult} />;
      })}
    </>
  );
}

function SingleDocPoller({ doc, localFile, onResult }) {
  const {
    status, data, accuracy, paymentsDetected, finding,
    documentType, policy, warnings,
  } = useExtractionPolling(doc.id, true);
  const reportedRef = useRef(false);
  useEffect(() => {
    if (status !== 'done' && status !== 'failed') return;
    if (reportedRef.current) return;
    reportedRef.current = true;
    onResult(localFile, doc.id, {
      status, data, accuracy, finding,
      documentType, policy, paymentsDetected, warnings,
    });
  }, [status, data, accuracy, paymentsDetected, finding, documentType, policy, warnings, localFile, doc.id, onResult]);
  return null;
}

/* ──────────────────────────────────────────────────────────
 * Sub-componente: paso DROP
 * ────────────────────────────────────────────────────────── */
function DropStep({
  files, setFiles, useAi, setUseAi, draggedOver, setDraggedOver,
  fileInputRef, handleSelectFiles, handleDrop,
}) {
  return (
    <>
      <div
        onDragOver={(e) => { e.preventDefault(); setDraggedOver(true); }}
        onDragLeave={() => setDraggedOver(false)}
        onDrop={handleDrop}
        onClick={() => fileInputRef.current?.click()}
        style={{
          border: `2px dashed ${draggedOver ? 'var(--pei-green-bright)' : 'var(--pei-cyan-bright)'}`,
          background: draggedOver ? 'rgba(34,236,154,0.06)' : 'rgba(50,184,223,0.04)',
          borderRadius: 16,
          padding: files.length === 0 ? '54px 24px' : '20px 24px',
          textAlign: 'center',
          transition: 'all 220ms',
          cursor: 'pointer',
          marginBottom: 16,
        }}
      >
        <input ref={fileInputRef} type="file" multiple accept="application/pdf"
          style={{ display: 'none' }} onChange={(e) => handleSelectFiles(e.target.files)} />
        {files.length === 0 ? (
          <>
            <div style={{ width: 56, height: 56, borderRadius: 16, background: 'rgba(50,184,223,0.14)', margin: '0 auto 16px', display: 'grid', placeItems: 'center', color: 'var(--pei-cyan)' }}>
              <i className="pi pi-cloud-upload" style={{ fontSize: 24 }} />
            </div>
            <div style={{ fontSize: 15, fontWeight: 600, color: 'var(--pei-text)' }}>Arrastra los PDFs aquí</div>
            <div style={{ fontSize: 13, color: 'var(--pei-text-helper)', marginTop: 4 }}>o haz click para seleccionar archivos</div>
            <div style={{ fontSize: 11, color: 'var(--pei-text-helper)', marginTop: 10 }}>PDF · máx 25 MB por archivo</div>
          </>
        ) : (
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, justifyContent: 'center', color: 'var(--pei-cyan)' }}>
            <i className="pi pi-cloud-upload" style={{ fontSize: 18 }} />
            <span style={{ fontSize: 13, fontWeight: 500, color: 'var(--pei-text-label)' }}>
              Arrastra más archivos o click para agregar
            </span>
          </div>
        )}
      </div>

      {files.length > 0 && (
        <div style={{ marginBottom: 16 }}>
          <div className="pei-label" style={{ marginBottom: 10 }}>
            DOCUMENTOS · {files.length} archivo{files.length === 1 ? '' : 's'}
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {files.map(f => (
              <FileRow
                key={f.id}
                f={f}
                onTypeChange={(type) => setFiles(files.map(x => x.id === f.id ? { ...x, type, year: type === 'pagare' ? (x.year || 1) : null } : x))}
                onYearChange={(year) => setFiles(files.map(x => x.id === f.id ? { ...x, year: parseInt(year, 10) || 1 } : x))}
                onRemove={() => setFiles(files.filter(x => x.id !== f.id))}
              />
            ))}
          </div>
        </div>
      )}

      <div style={{
        background: useAi ? 'rgba(34,236,154,0.08)' : 'var(--pei-bg-soft)',
        border: `1.5px solid ${useAi ? 'rgba(34,236,154,0.40)' : 'var(--pei-gray-5)'}`,
        borderRadius: 14,
        padding: '14px 16px',
        display: 'flex', alignItems: 'center', gap: 14,
        transition: 'all 220ms',
      }}>
        <div style={{
          width: 38, height: 38, borderRadius: 10,
          background: useAi ? 'var(--pei-gradient-mint-cyan)' : 'var(--pei-gray-5)',
          color: 'white',
          display: 'grid', placeItems: 'center', flexShrink: 0,
        }}>
          <i className="pi pi-bolt" style={{ fontSize: 16 }} />
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--pei-text)' }}>Usar IA para extraer datos</div>
          <div style={{ fontSize: 12, color: 'var(--pei-text-helper)', marginTop: 2 }}>
            Lee póliza, fechas, total y pagarés del PDF en ~15 segundos.
          </div>
        </div>
        <Toggle value={useAi} onChange={setUseAi} />
      </div>
    </>
  );
}

function FileRow({ f, onTypeChange, onYearChange, onRemove }) {
  const isUnrelated = !!f.aiUnrelated;
  return (
    <div style={{
      display: 'flex', alignItems: 'center', gap: 12,
      padding: '10px 14px 10px 10px',
      background: isUnrelated ? 'var(--pei-bg-soft)' : 'white',
      border: '1px solid var(--pei-gray-5)',
      borderRadius: 12,
      flexWrap: 'wrap',
      opacity: isUnrelated ? 0.7 : 1,
    }}>
      <div style={{
        width: 40, height: 52, borderRadius: 6,
        background: '#fff5f5', border: '1px solid #fecaca',
        display: 'grid', placeItems: 'center',
        color: 'var(--pei-danger)',
        flexShrink: 0,
        fontSize: 10, fontWeight: 700,
        letterSpacing: '0.05em',
      }}>
        PDF
      </div>
      <div style={{ flex: 1, minWidth: 160 }}>
        <div style={{ fontSize: 13.5, fontWeight: 600, color: 'var(--pei-text)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', display: 'flex', alignItems: 'center', gap: 6 }}>
          {f.nombre}
          {f.aiDetected && !isUnrelated && (
            <span title="Detectado por IA" style={{ fontSize: 10, padding: '2px 6px', background: 'rgba(34,236,154,0.20)', color: '#0a7c4c', borderRadius: 999, fontWeight: 700, letterSpacing: '0.04em' }}>
              <i className="pi pi-bolt" style={{ fontSize: 9, marginRight: 3 }} /> detectado
            </span>
          )}
          {isUnrelated && (
            <span title="La IA detectó que no es póliza ni pagaré" style={{ fontSize: 10, padding: '2px 6px', background: 'var(--pei-gray-5)', color: 'var(--pei-text-helper)', borderRadius: 999, fontWeight: 700, letterSpacing: '0.04em' }}>
              ajeno
            </span>
          )}
        </div>
        <div style={{ fontSize: 11, color: 'var(--pei-text-helper)', marginTop: 2 }}>{f.size}</div>
        {isUnrelated && (
          <div style={{ fontSize: 11, color: 'var(--pei-text-helper)', marginTop: 4, fontStyle: 'italic' }}>
            Este documento no parece ser póliza ni pagaré. Si es un endoso, súbelo después con la acción Modificar.
          </div>
        )}
      </div>

      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
        <span style={{ fontSize: 11, fontWeight: 600, color: 'var(--pei-text-label)', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Tipo</span>
        <select
          value={f.type}
          onChange={(e) => onTypeChange(e.target.value)}
          style={{
            height: 32, padding: '0 8px',
            border: '1.5px solid var(--pei-cyan-bright)',
            borderRadius: 8, fontSize: 13, fontFamily: 'inherit',
            background: 'white', color: 'var(--pei-text)', cursor: 'pointer', outline: 'none',
          }}
        >
          {DOC_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
        </select>
      </div>

      {f.type === 'pagare' && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <span style={{ fontSize: 11, fontWeight: 600, color: 'var(--pei-text-label)', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Año</span>
          <input
            type="number"
            min={1}
            max={10}
            value={f.year ?? 1}
            onChange={(e) => onYearChange(e.target.value)}
            style={{
              width: 56, height: 32,
              border: '1.5px solid var(--pei-cyan-bright)',
              borderRadius: 8, padding: '0 8px',
              fontSize: 13, color: 'var(--pei-text)',
              background: 'white', fontFamily: 'inherit', outline: 'none',
              textAlign: 'center',
            }}
          />
        </div>
      )}

      <button onClick={onRemove} style={{
        width: 32, height: 32, borderRadius: 8,
        background: 'transparent', border: 'none',
        color: 'var(--pei-text-helper)', cursor: 'pointer',
        display: 'grid', placeItems: 'center',
      }} title="Quitar archivo">
        <i className="pi pi-trash" style={{ fontSize: 13 }} />
      </button>
    </div>
  );
}

function Toggle({ value, onChange }) {
  return (
    <button onClick={() => onChange(!value)} type="button" style={{
      width: 42, height: 24, borderRadius: 12,
      background: value ? 'var(--pei-green-bright)' : 'var(--pei-gray-3)',
      border: 'none', cursor: 'pointer',
      position: 'relative',
      transition: 'background 200ms',
      flexShrink: 0,
    }}>
      <span style={{
        position: 'absolute', top: 2, left: value ? 20 : 2,
        width: 20, height: 20, borderRadius: 10,
        background: 'white',
        boxShadow: '0 1px 3px rgba(0,0,0,0.18)',
        transition: 'left 200ms',
      }} />
    </button>
  );
}

/* ──────────────────────────────────────────────────────────
 * Sub-componente: PROCESSING
 * ────────────────────────────────────────────────────────── */
function ProcessingStep({ files, useAi, aiBanners }) {
  return (
    <>
      <div className="banner info" style={{ marginBottom: 18 }}>
        <i className="pi pi-info-circle" />
        <div>
          <div className="b-title">Esto suele tardar 10–20 segundos</div>
          <div className="b-sub">Los datos extraídos serán editables antes de completar el paso.</div>
        </div>
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        {files.map(f => {
          const docId = f.doc?.id ? String(f.doc.id) : null;
          const banner = docId ? aiBanners[docId] : null;
          const done = banner && (banner.failed || banner.accuracy != null);
          return (
            <div key={f.id} style={{
              display: 'flex', alignItems: 'center', gap: 14,
              padding: '14px 16px',
              background: 'white',
              border: '1px solid var(--pei-gray-5)',
              borderRadius: 14,
            }}>
              <div style={{ position: 'relative', width: 40, height: 52, flexShrink: 0 }}>
                <div style={{ width: 40, height: 52, borderRadius: 6, background: '#fff5f5', border: '1px solid #fecaca', display: 'grid', placeItems: 'center', color: 'var(--pei-danger)', fontSize: 10, fontWeight: 700, letterSpacing: '0.05em' }}>PDF</div>
                {!done && (
                  <div style={{ position: 'absolute', inset: 0, background: 'rgba(255,255,255,0.85)', display: 'grid', placeItems: 'center', borderRadius: 6 }}>
                    <div className="spinner-cyan" />
                  </div>
                )}
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 13.5, fontWeight: 600, color: 'var(--pei-text)' }}>{f.nombre}</div>
                <div style={{ fontSize: 12, color: done ? 'var(--pei-green-true)' : 'var(--pei-cyan)', marginTop: 4, display: 'flex', alignItems: 'center', gap: 6 }}>
                  <i className={`pi ${done ? 'pi-check' : 'pi-bolt'}`} style={{ fontSize: 11 }} />
                  {!useAi ? 'Subiendo archivo…' : (done
                    ? (banner.failed ? 'IA no procesó este PDF' : `IA terminó · ${banner.accuracy ?? '—'}%`)
                    : 'Extrayendo datos…')}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </>
  );
}

/* ──────────────────────────────────────────────────────────
 * Sub-componente: REVIEW
 * ────────────────────────────────────────────────────────── */
function ReviewStep(props) {
  const {
    files, providers,
    providerId, setProviderId, providerFree, setProviderFree,
    policyNumber, setPolicyNumber,
    coverageType, setCoverageType,
    startDate, setStartDate,
    endDate, setEndDate,
    annualTotal, setAnnualTotal,
    paymentPlanYears, setPaymentPlanYears,
    payments, setPayments,
    paymentsSum, totalEsperado, sumDiff, sumOk,
    totalFinal, aiTotal, aiTotalMismatch,
    anyLowConfidence, useAi, cotizadorPrice, aiBanners,
    globalWarnings,
  } = props;

  const years = Math.max(1, Math.min(10, Number(paymentPlanYears) || 1));
  const cards = Array.from({ length: years }, (_, i) => payments[i + 1]).filter(Boolean);

  // Opciones del dropdown de PDF (todos los uploaded, excluyendo unrelated)
  const pdfOptions = files.filter((f) => !!f.doc && !f.aiUnrelated);

  // Lista de docs marcados como unrelated (para mostrar mensaje)
  const unrelatedFiles = files.filter((f) => f.aiUnrelated);

  // Banner global IA: si algún doc quedó con accuracy < 30 o failed
  const showAiWarning = useAi && (anyLowConfidence || Object.values(aiBanners).some((b) => b?.failed));

  return (
    <>
      {showAiWarning && (
        <div className="banner warning" style={{ marginBottom: 18 }}>
          <i className="pi pi-exclamation-triangle" />
          <div>
            <div className="b-title">La IA no leyó alguno de los PDFs con suficiente confianza</div>
            <div className="b-sub">Revisa cada campo antes de guardar. No bloqueamos el envío.</div>
          </div>
        </div>
      )}

      {Array.isArray(globalWarnings) && globalWarnings.length > 0 && (
        <div className="banner warning" style={{ marginBottom: 18 }}>
          <i className="pi pi-exclamation-triangle" />
          <div>
            <div className="b-title">Avisos de la extracción IA</div>
            <ul style={{ margin: '6px 0 0 18px', padding: 0, fontSize: 12, color: 'var(--pei-text-helper)' }}>
              {globalWarnings.slice(0, 6).map((w, i) => (<li key={i}>{w}</li>))}
              {globalWarnings.length > 6 && (
                <li>… y {globalWarnings.length - 6} más</li>
              )}
            </ul>
          </div>
        </div>
      )}

      {unrelatedFiles.length > 0 && (
        <div className="banner info" style={{ marginBottom: 18, opacity: 0.85 }}>
          <i className="pi pi-info-circle" />
          <div>
            <div className="b-title">{unrelatedFiles.length} documento{unrelatedFiles.length === 1 ? '' : 's'} no parece ser póliza ni pagaré</div>
            <div className="b-sub">
              Si es un endoso, súbelo después con la acción <strong>Modificar</strong>.
              {' '}({unrelatedFiles.map((f) => f.nombre).join(', ')})
            </div>
          </div>
        </div>
      )}

      {/* Datos de la póliza */}
      <div className="pei-label" style={{ marginBottom: 10 }}>DATOS DE LA PÓLIZA</div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
        <ProviderField
          providers={providers}
          providerId={providerId} setProviderId={setProviderId}
          providerFree={providerFree} setProviderFree={setProviderFree}
        />
        <EditableField label="N° de póliza" value={policyNumber} onChange={setPolicyNumber} mono required />
        <SelectField label="Cobertura" value={coverageType} onChange={setCoverageType} options={COVERAGE_OPTIONS} />
        <EditableField label="Plan (años)" value={String(paymentPlanYears)} onChange={(v) => setPaymentPlanYears(Math.max(1, Math.min(10, parseInt(v, 10) || 1)))} type="number" required />
        <EditableField label="Inicio" value={startDate} onChange={setStartDate} type="date" required />
        <EditableField label="Fin" value={endDate} onChange={setEndDate} type="date" required />
        <ComputedTotalField
          label="Total (MXN)"
          value={totalFinal}
          aiValue={aiTotal}
          mismatch={aiTotalMismatch}
        />
      </div>

      {Number(cotizadorPrice) > 0 && totalEsperado > 0 && Math.abs(Number(cotizadorPrice) - totalEsperado) > 1 && (
        <div className="banner warning" style={{ marginTop: 14 }}>
          <i className="pi pi-exclamation-triangle" />
          <div>
            <div className="b-title">Total difiere del cotizado</div>
            <div className="b-sub">
              Cotizador: <strong>{fmtMxn(Number(cotizadorPrice))}</strong> · Total póliza: <strong>{fmtMxn(totalEsperado)}</strong>
            </div>
          </div>
        </div>
      )}

      {/* Pagarés */}
      <div className="pei-label" style={{ marginTop: 22, marginBottom: 10 }}>
        PAGARÉS ({cards.length})
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        {cards.map((p) => (
          <PaymentCard
            key={p.period_number}
            payment={p}
            pdfOptions={pdfOptions}
            onChange={(patch) => {
              setPayments((prev) => ({
                ...prev,
                [p.period_number]: { ...prev[p.period_number], ...patch },
              }));
            }}
          />
        ))}
      </div>

      {/* Footer suma */}
      <div style={{
        marginTop: 16,
        padding: '12px 16px',
        background: totalEsperado === 0
          ? 'var(--pei-bg-soft)'
          : sumOk ? 'rgba(34,236,154,0.10)' : 'rgba(220,38,38,0.06)',
        border: `1px solid ${totalEsperado === 0 ? 'var(--pei-gray-5)' : sumOk ? 'rgba(34,236,154,0.30)' : 'rgba(220,38,38,0.30)'}`,
        borderRadius: 12,
        display: 'flex', alignItems: 'center', gap: 10,
        fontSize: 13,
      }}>
        <i className={`pi ${sumOk ? 'pi-check-circle' : 'pi-exclamation-circle'}`}
          style={{ fontSize: 14, color: sumOk ? 'var(--pei-green-true)' : 'var(--pei-danger)' }} />
        <div>
          <strong>Suma pagarés: {fmtMxn(paymentsSum)}</strong>
          {' · '}
          {totalEsperado === 0
            ? <span>Captura el total para validar</span>
            : sumOk
              ? <span style={{ color: '#0a7c4c' }}>coincide con total ({fmtMxn(totalEsperado)})</span>
              : <span style={{ color: 'var(--pei-danger)' }}>
                  difiere por {fmtMxn(Math.abs(sumDiff))} {sumDiff > 0 ? '(sobra)' : '(falta)'}
                </span>}
        </div>
      </div>
    </>
  );
}

function PaymentCard({ payment, pdfOptions, onChange }) {
  const isProjected = !!payment.projected;
  return (
    <div style={{
      border: `1px solid ${isProjected ? 'rgba(168, 85, 247, 0.40)' : 'var(--pei-gray-5)'}`,
      borderRadius: 14,
      padding: '12px 14px',
      background: isProjected ? 'rgba(168, 85, 247, 0.04)' : 'white',
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
        <span style={{
          fontSize: 11, fontWeight: 700, color: 'var(--pei-cyan)',
          background: 'rgba(50,184,223,0.12)',
          padding: '3px 8px', borderRadius: 999,
          letterSpacing: '0.05em', textTransform: 'uppercase',
        }}>
          Año {payment.period_number}
        </span>
        {isProjected && (
          <span title="Monto proyectado desde la carátula. Súbe el pagaré real para confirmar." style={{
            fontSize: 10, fontWeight: 700,
            color: '#7c3aed',
            background: 'rgba(168, 85, 247, 0.14)',
            padding: '3px 8px', borderRadius: 999,
            letterSpacing: '0.04em', textTransform: 'uppercase',
          }}>
            <i className="pi pi-clock" style={{ fontSize: 9, marginRight: 3 }} /> proyectado
          </span>
        )}
        <input
          value={payment.period_label || ''}
          onChange={(e) => onChange({ period_label: e.target.value })}
          placeholder={fmtPeriodLabel(payment.period_number, payment.due_date)}
          style={{
            flex: 1, height: 28,
            border: '1px solid transparent',
            borderRadius: 6, padding: '0 8px',
            fontSize: 12, fontWeight: 600,
            color: 'var(--pei-text)',
            background: 'transparent', outline: 'none', fontFamily: 'inherit',
          }}
        />
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
        <FieldSmall label="Inicio de cobertura">
          <input
            type="date"
            value={payment.period_start || ''}
            onChange={(e) => onChange({ period_start: e.target.value })}
            style={inputStyle()}
          />
        </FieldSmall>
        <FieldSmall label="Vence" required>
          <input
            type="date"
            value={payment.due_date || ''}
            onChange={(e) => {
              const v = e.target.value;
              const y = yearOf(v);
              onChange({
                due_date: v,
                period_end: v,
                period_label: payment.period_label || (y ? `Año ${payment.period_number} (${y})` : payment.period_label),
              });
            }}
            style={inputStyle()}
          />
        </FieldSmall>
        <FieldSmall label="Monto (MXN)" required>
          <input
            type="number"
            step="0.01"
            min="0"
            value={payment.amount || ''}
            onChange={(e) => onChange({ amount: e.target.value })}
            placeholder="$0.00"
            style={inputStyle()}
          />
        </FieldSmall>
        <FieldSmall label="Folio (opcional)">
          <input
            type="text"
            value={payment.reference_number || ''}
            onChange={(e) => onChange({ reference_number: e.target.value })}
            placeholder="—"
            style={inputStyle()}
          />
        </FieldSmall>
        <FieldSmall label="PDF de respaldo">
          <select
            value={payment.document_local_id || ''}
            onChange={(e) => onChange({ document_local_id: e.target.value || null })}
            style={{ ...inputStyle(), cursor: 'pointer' }}
          >
            <option value="">— Sin asignar —</option>
            {pdfOptions.map((f) => (
              <option key={f.id} value={f.id}>
                {f.nombre} · {f.type}
                {f.type === 'pagare' && f.year ? ` (año ${f.year})` : ''}
              </option>
            ))}
          </select>
        </FieldSmall>
      </div>
    </div>
  );
}

function FieldSmall({ label, required, children }) {
  return (
    <div>
      <div style={{ fontSize: 10.5, fontWeight: 600, color: 'var(--pei-text-helper)', letterSpacing: '0.04em', textTransform: 'uppercase', marginBottom: 4 }}>
        {label}{required && <span style={{ color: 'var(--pei-danger)', marginLeft: 4 }}>*</span>}
      </div>
      {children}
    </div>
  );
}

function inputStyle() {
  return {
    width: '100%', height: 34, padding: '0 10px',
    border: '1.5px solid var(--pei-cyan-bright)',
    borderRadius: 8,
    background: 'white',
    fontSize: 13, fontWeight: 500,
    color: 'var(--pei-text)',
    outline: 'none',
    fontFamily: 'inherit',
  };
}

function ProviderField({ providers, providerId, setProviderId, providerFree, setProviderFree }) {
  return (
    <div>
      <div className="pei-label">Aseguradora</div>
      <select
        className="pei-select"
        value={providerId === '' ? '' : String(providerId)}
        onChange={(e) => setProviderId(e.target.value)}
        style={{ height: 38, borderColor: 'var(--pei-cyan-bright)', borderWidth: 1.5 }}
      >
        <option value="">Selecciona…</option>
        {providers.map(p => <option key={p.id} value={String(p.id)}>{p.name}</option>)}
        <option value="__free__">+ Otro (texto libre)</option>
      </select>
      {providerId === '__free__' && (
        <input className="pei-input" value={providerFree} onChange={(e) => setProviderFree(e.target.value)} placeholder="Nombre libre" style={{ marginTop: 6, height: 38 }} />
      )}
    </div>
  );
}

function EditableField({ label, value, onChange, placeholder, mono, type = 'text', warn, required }) {
  const isEmpty = value == null || value === '' || value === '—';
  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 6, minHeight: 16 }}>
        <span style={{ fontSize: 11, fontWeight: 600, color: 'var(--pei-text-label)', letterSpacing: '0.05em', textTransform: 'uppercase' }}>
          {label}{required && isEmpty && <span style={{ color: 'var(--pei-danger)', marginLeft: 4 }}>*</span>}
        </span>
      </div>
      <input
        type={type}
        value={value == null ? '' : value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder || 'Capturar…'}
        style={{
          width: '100%', height: 38, padding: '0 12px',
          border: `1.5px solid ${warn ? '#FCD34D' : 'var(--pei-cyan-bright)'}`,
          borderRadius: 10,
          background: warn ? 'rgba(252,211,77,0.10)' : 'white',
          fontFamily: mono ? 'var(--pei-font-mono)' : 'inherit',
          fontSize: mono ? 13 : 14,
          fontWeight: 500,
          color: 'var(--pei-text)',
          outline: 'none',
        }}
      />
    </div>
  );
}

/**
 * Campo de Total calculado automáticamente desde la suma de los pagarés.
 * Es readonly por diseño: los pagarés son la fuente de verdad.
 * Si la IA detectó un total distinto al de la suma, muestra un aviso pequeño.
 */
function ComputedTotalField({ label, value, aiValue, mismatch }) {
  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 6, minHeight: 16 }}>
        <span style={{ fontSize: 11, fontWeight: 600, color: 'var(--pei-text-label)', letterSpacing: '0.05em', textTransform: 'uppercase' }}>
          {label}
        </span>
        <span style={{ fontSize: 10, color: 'var(--pei-text-helper)', fontStyle: 'italic' }}>
          (auto · suma de pagarés)
        </span>
      </div>
      <div
        style={{
          width: '100%', height: 38, padding: '0 12px',
          border: '1.5px solid var(--pei-gray-5)',
          borderRadius: 10,
          background: 'var(--pei-bg-soft)',
          display: 'flex', alignItems: 'center',
          fontSize: 14, fontWeight: 600,
          color: 'var(--pei-text)',
        }}
      >
        {fmtMxn(Number(value) || 0)}
      </div>
      {mismatch && (
        <div style={{ fontSize: 11, color: '#92400E', marginTop: 4, lineHeight: 1.3 }}>
          <i className="pi pi-exclamation-triangle" style={{ fontSize: 11, marginRight: 4 }} />
          La póliza decía {fmtMxn(aiValue)}, pero los pagarés suman {fmtMxn(Number(value) || 0)}. Se usará la suma.
        </div>
      )}
    </div>
  );
}

function SelectField({ label, value, onChange, options }) {
  return (
    <div>
      <div style={{ marginBottom: 6, fontSize: 11, fontWeight: 600, color: 'var(--pei-text-label)', letterSpacing: '0.05em', textTransform: 'uppercase' }}>
        {label}
      </div>
      <select value={value} onChange={(e) => onChange(e.target.value)}
        style={{
          width: '100%', height: 38, padding: '0 12px',
          border: '1.5px solid var(--pei-cyan-bright)',
          borderRadius: 10, fontSize: 14, fontFamily: 'inherit',
          background: 'white', color: 'var(--pei-text)', cursor: 'pointer', outline: 'none',
        }}>
        <option value="">Selecciona…</option>
        {options.map(opt => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
      </select>
    </div>
  );
}
